import { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { toast } from "sonner";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "../components/ui/sheet";
import { api, errMsg, download, useApi } from "../lib/api";
import { useAuth, hasRole } from "../lib/store";
import { SCHEMAS } from "./schemas";
import { ImagesField, MediaField, VariantsField, LinksField, TermsField, OffersField } from "./Fields";

const inp = "h-10 w-full border border-[#E6DFD5] bg-white px-3";

function useRefs(schema) {
  const [refs, setRefs] = useState({});
  useEffect(() => {
    const ents = [...new Set(schema.fields.filter((f) => f.ref).map((f) => f.ref))];
    ents.forEach((e) => api.get(`/admin/e/${e}?limit=500`).then((r) => setRefs((x) => ({ ...x, [e]: r.data.items }))).catch(() => {}));
    if (schema.fields.some((f) => f.t === "offers")) api.get("/print-products").then((r) => setRefs((x) => ({ ...x, __products: r.data }))).catch(() => {});
  }, [schema]);
  return refs;
}

const refLabel = (d) => d?.title || d?.name || d?.label || d?.start_date || d?.id?.slice(0, 8);

function FieldInput({ f, v, set, refs, ctx }) {
  const id = `f-${f.k}`;
  const tid = `field-${f.k}`;
  switch (f.t) {
    case "textarea": return <textarea id={id} rows={f.rows || 4} value={v || ""} onChange={(e) => set(e.target.value)} className={`${inp} h-auto py-2`} data-testid={tid} />;
    case "number": return <input id={id} type="number" step="any" value={v ?? ""} onChange={(e) => set(e.target.value)} className={inp} data-testid={tid} />;
    case "date": return <input id={id} type="date" value={v || ""} onChange={(e) => set(e.target.value)} className={inp} data-testid={tid} />;
    case "datetime": return <input id={id} type="datetime-local" value={v || ""} onChange={(e) => set(e.target.value)} className={inp} data-testid={tid} />;
    case "bool": return <input id={id} type="checkbox" checked={!!v} onChange={(e) => set(e.target.checked)} className="h-5 w-5" data-testid={tid} />;
    case "select": return <select id={id} value={v || ""} onChange={(e) => set(e.target.value)} className={inp} data-testid={tid}><option value="">–</option>{f.o.map(([a, b]) => <option key={a} value={a}>{b}</option>)}</select>;
    case "ref": return <select id={id} value={v || ""} onChange={(e) => set(e.target.value)} className={inp} data-testid={tid}><option value="">–</option>{(refs[f.ref] || []).map((d) => <option key={d.id} value={d.id}>{refLabel(d)}</option>)}</select>;
    case "refs": return <select id={id} multiple value={v || []} onChange={(e) => set([...e.target.selectedOptions].map((o) => o.value))} className={`${inp} h-28`} data-testid={tid}>{(refs[f.ref] || []).map((d) => <option key={d.id} value={d.id}>{refLabel(d)}</option>)}</select>;
    case "images": return <ImagesField value={v} onChange={set} testid={tid} />;
    case "image": return <ImagesField value={v} onChange={set} single testid={tid} />;
    case "media": return <MediaField value={v} onChange={set} kind={f.kind} ctx={ctx} testid={tid} />;
    case "variants": return <VariantsField value={v || []} onChange={set} />;
    case "links": return <LinksField value={v || []} onChange={set} />;
    case "terms": return <TermsField value={v} onChange={set} />;
    case "offers": return <OffersField value={v} onChange={set} products={refs.__products || []} />;
    case "tags": return <input id={id} value={(v || []).join(", ")} onChange={(e) => set(e.target.value.split(",").map((s) => s.trim()).filter(Boolean))} className={inp} data-testid={tid} />;
    case "socials": return <div className="grid grid-cols-2 gap-2">{["instagram", "facebook", "tiktok", "other"].map((s) => <input key={s} placeholder={s} value={(v || {})[s] || ""} onChange={(e) => set({ ...(v || {}), [s]: e.target.value })} className={inp} />)}</div>;
    default: return <input id={id} value={v ?? ""} onChange={(e) => set(e.target.value)} className={inp} data-testid={tid} />;
  }
}

export function EntityForm({ entity, item, onSaved, onClose }) {
  const schema = SCHEMAS[entity];
  const { user } = useAuth();
  const refs = useRefs(schema);
  const [d, setD] = useState(item || {});
  const [busy, setBusy] = useState(false);
  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      let payload = { ...d };
      if (entity === "qr_codes" && item?.id && d.artwork_id !== item.artwork_id) {
        if (!window.confirm("Ziel dieses gedruckten QR-Codes wirklich ändern? Die Änderung wird protokolliert.")) return setBusy(false);
        payload.confirm_retarget = true;
      }
      const r = item?.id ? await api.put(`/admin/e/${entity}/${item.id}`, payload) : await api.post(`/admin/e/${entity}`, payload);
      toast.success("Gespeichert");
      onSaved(r.data);
    } catch (e2) {
      toast.error(errMsg(e2));
    } finally {
      setBusy(false);
    }
  };
  return (
    <form onSubmit={save} className="space-y-4 pb-24" data-testid="entity-form">
      {schema.fields.filter((f) => !f.owner || hasRole(user)).map((f) => (
        <div key={f.k} className={f.t === "bool" ? "flex items-center gap-3 flex-row-reverse justify-end" : ""}>
          <label htmlFor={`f-${f.k}`} className="text-sm font-semibold block mb-1">{f.l}</label>
          <FieldInput f={f} v={d[f.k]} set={(v) => setD((x) => ({ ...x, [f.k]: v }))} refs={refs} ctx={{ entity, id: item?.id }} />
          {f.hint && <p className="text-xs text-[#6E6B65] mt-1">{f.hint}</p>}
        </div>
      ))}
      <div className="fixed bottom-0 right-0 w-full sm:max-w-2xl bg-[#FAF8F5] border-t border-[#E6DFD5] p-4 flex gap-3">
        <button disabled={busy} className="btn-ink h-11 px-6 font-semibold" data-testid="entity-save">{busy ? "Speichert …" : "Speichern"}</button>
        <button type="button" onClick={onClose} className="h-11 px-4 border">Abbrechen</button>
      </div>
    </form>
  );
}

function ImportBox({ entity, onDone }) {
  const [rep, setRep] = useState(null);
  const [file, setFile] = useState(null);
  const run = async (commit) => {
    const fd = new FormData();
    fd.append("file", file);
    try {
      const r = await api.post(`/admin/e/${entity}/import?commit=${commit}`, fd);
      setRep(r.data);
      if (commit) { toast.success("Import abgeschlossen"); onDone(); }
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  return (
    <div className="bg-white border border-[#E6DFD5] p-4 mb-4 text-sm" data-testid="import-box">
      <p className="font-semibold mb-2">CSV-Import (Trennzeichen ; oder ,) – Vorschau mit Prüfung, Import nur ohne Fehler, Datensätze als Entwurf</p>
      <input type="file" accept=".csv,text/csv" onChange={(e) => setFile(e.target.files[0])} data-testid="import-file" />
      <button disabled={!file} onClick={() => run(false)} className="ml-3 h-9 px-3 border-2 border-[#121212] font-semibold" data-testid="import-preview">Vorschau</button>
      {rep && <>
        <table className="mt-3 w-full text-xs"><tbody>{rep.rows.map((r) => <tr key={r.line} className={r.errors.length ? "bg-[#FFEAEA]" : ""}><td className="p-1">Zeile {r.line}</td><td className="p-1">{Object.values(r.data).filter(Boolean).slice(0, 3).join(" · ")}</td><td className="p-1 text-[#c2181d]">{r.errors.join("; ")}</td><td className="p-1 text-[#6E6B65]">{r.warnings.join("; ")}</td></tr>)}</tbody></table>
        <button disabled={!rep.valid} onClick={() => run(true)} className="mt-3 btn-ink h-9 px-4 font-semibold disabled:opacity-40" data-testid="import-commit">{rep.valid ? `${rep.rows.length} Zeilen importieren` : "Fehler korrigieren"}</button>
      </>}
    </div>
  );
}

export default function EntityManager({ entityProp }) {
  const params = useParams();
  const entity = entityProp || params.entity;
  const schema = SCHEMAS[entity];
  const { user } = useAuth();
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("");
  const [edit, setEdit] = useState(null);
  const [imp, setImp] = useState(false);
  const { data, loading, error, reload } = useApi(schema ? `/admin/e/${entity}?q=${encodeURIComponent(q)}&limit=200${status ? `&status=${status}` : ""}` : null);
  const refs = useRefs(schema || { fields: [] });
  const lookup = useMemo(() => Object.fromEntries(Object.values(refs).flat().map((d) => [d.id, refLabel(d)])), [refs]);
  if (!schema) return <p>Unbekannter Bereich.</p>;
  const del = async (it) => {
    if (!window.confirm(`„${it[schema.title] || it.id}“ wirklich löschen?`)) return;
    try {
      await api.delete(`/admin/e/${entity}/${it.id}?confirm=true`);
      toast.success("Gelöscht");
      reload();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  const fmt = (k, v) => (typeof v === "boolean" ? (v ? "Ja" : "–") : k.endsWith("_id") ? lookup[v] || "–" : v ?? "–");
  return (
    <div data-testid={`entity-${entity}`}>
      <div className="flex flex-wrap items-end gap-3 mb-6">
        <h1 className="text-3xl font-black mr-auto">{schema.label}</h1>
        <input type="search" placeholder="Suchen …" value={q} onChange={(e) => setQ(e.target.value)} className="h-10 border border-[#E6DFD5] px-3" data-testid="entity-search" />
        {schema.fields.some((f) => f.k === "status") && <select value={status} onChange={(e) => setStatus(e.target.value)} className="h-10 border border-[#E6DFD5] px-2" data-testid="entity-status-filter"><option value="">Alle Status</option>{schema.fields.find((f) => f.k === "status").o.map(([a, b]) => <option key={a} value={a}>{b}</option>)}</select>}
        <button onClick={() => download(`/admin/e/${entity}/export.csv`, `${entity}.csv`).catch((e) => toast.error(errMsg(e)))} className="h-10 px-3 border-2 border-[#121212] text-sm font-semibold" data-testid="entity-export">CSV-Export</button>
        {["artists", "artworks", "venues", "finds", "events", "customers"].includes(entity) && <button onClick={() => setImp(!imp)} className="h-10 px-3 border-2 border-[#121212] text-sm font-semibold" data-testid="entity-import-toggle">CSV-Import</button>}
        <button onClick={() => setEdit({})} className="btn-ink h-10 px-4 font-semibold" data-testid="entity-new">+ Neu</button>
      </div>
      {imp && <ImportBox entity={entity} onDone={reload} />}
      {error ? <p className="text-[#c2181d]" data-testid="entity-error">{error}</p> : loading ? <p>Lädt …</p> : (
        <div className="overflow-x-auto bg-white border border-[#E6DFD5]">
          <table className="w-full text-sm" data-testid="entity-table">
            <thead className="bg-[#F4EFEA] text-left"><tr>{schema.cols.map((c) => <th key={c} className="p-3 font-semibold">{schema.fields.find((f) => f.k === c)?.l?.replace(" *", "") || c}</th>)}<th className="p-3" /></tr></thead>
            <tbody>{data.items.map((it) => (
              <tr key={it.id} className="border-t border-[#E6DFD5]" data-testid={`row-${it.slug || it.id}`}>
                {schema.cols.map((c) => <td key={c} className="p-3">{String(fmt(c, it[c]))}</td>)}
                <td className="p-3 text-right whitespace-nowrap">
                  {entity === "artworks" && it.slug && it.status === "published" && <a href={`/kunst/${it.slug}`} target="_blank" rel="noreferrer" className="underline mr-3">Ansehen</a>}
                  <button onClick={() => setEdit(it)} className="underline font-semibold mr-3" data-testid="row-edit">Bearbeiten</button>
                  {hasRole(user, "editor", "george", "venue_manager") && entity !== "qr_codes" && <button onClick={() => del(it)} className="text-[#c2181d] underline" data-testid="row-delete">Löschen</button>}
                </td>
              </tr>))}
              {!data.items.length && <tr><td colSpan={9} className="p-8 text-center text-[#6E6B65]">Keine Einträge.</td></tr>}
            </tbody>
          </table>
        </div>
      )}
      <Sheet open={!!edit} onOpenChange={(o) => !o && setEdit(null)}>
        <SheetContent className="w-full sm:max-w-2xl overflow-y-auto bg-[#FAF8F5]">
          <SheetHeader><SheetTitle className="font-display text-2xl">{edit?.id ? "Bearbeiten" : "Neu anlegen"} · {schema.label}</SheetTitle></SheetHeader>
          {edit && <div className="mt-6"><EntityForm key={edit.id || "new"} entity={entity} item={edit.id ? edit : null} onClose={() => setEdit(null)} onSaved={() => { setEdit(null); reload(); }} /></div>}
        </SheetContent>
      </Sheet>
    </div>
  );
}
